import { state, currentUserRole } from "../state";
import { apiCall } from "../api";
import { showToast } from "../utils";
import { createVirtualTable, getDisplayColumnName } from "../pages/common";
import { openRefRecordEditModal, openRefRecordEditModalSingle } from "../pages/admin";
import { classificationPicker, ClassificationPickerValue } from "./classification-picker";

function getEl<T extends HTMLElement>(id: string): T | null {
  return document.getElementById(id) as T | null;
}
function getInput(id: string): HTMLInputElement | null {
  return getEl<HTMLInputElement>(id);
}
function getSelect(id: string): HTMLSelectElement | null {
  return getEl<HTMLSelectElement>(id);
}
function getButton(id: string): HTMLButtonElement | null {
  return getEl<HTMLButtonElement>(id);
}

export interface TableEditorOpenOptions {
  source: "ref" | "req";
  group?: string;
  sessionId?: string;
  title?: string;
}

export class TableEditor {
  modal: HTMLElement;
  titleEl: HTMLElement;
  groupSelect: HTMLSelectElement;
  loadBtn: HTMLButtonElement;
  saveBtn: HTMLButtonElement;
  closeBtn: HTMLButtonElement;
  addRecordBtn: HTMLButtonElement;
  massReplaceBtn: HTMLButtonElement;
  bulkActions: HTMLElement;
  selectedCount: HTMLElement;
  columnPicker: HTMLElement;
  addColumnSelect: HTMLSelectElement;
  addColumnCustom: HTMLInputElement;
  addColumnBtn: HTMLButtonElement;
  removeColumnSelect: HTMLSelectElement;
  removeColumnBtn: HTMLButtonElement;
  container: HTMLElement;
  statusEl: HTMLElement;

  table: any = null;
  source: "ref" | "req" = "ref";
  sessionId: string | null = null;
  currentGroup: string | null = null;
  extraColumns: string[] = [];
  defaultColumns: string[] = [];
  allColumns: string[] = [];
  edits: Record<string, Record<string, any>> = {};
  isAdmin = false;

  constructor() {
    this.modal = getEl("table-editor-modal")!;
    this.titleEl = getEl("table-editor-title")!;
    this.groupSelect = getSelect("table-editor-group-select")!;
    this.loadBtn = getButton("table-editor-load")!;
    this.saveBtn = getButton("table-editor-save")!;
    this.closeBtn = getButton("table-editor-close")!;
    this.addRecordBtn = getButton("table-editor-add-record")!;
    this.massReplaceBtn = getButton("table-editor-mass-replace")!;
    this.bulkActions = getEl("table-editor-bulk-actions")!;
    this.selectedCount = getEl("table-editor-selected-count")!;
    this.columnPicker = getEl("table-editor-column-picker")!;
    this.addColumnSelect = getSelect("table-editor-add-column-select")!;
    this.addColumnCustom = getInput("table-editor-add-column-custom")!;
    this.addColumnBtn = getButton("table-editor-add-column-btn")!;
    this.removeColumnSelect = getSelect("table-editor-remove-column-select")!;
    this.removeColumnBtn = getButton("table-editor-remove-column-btn")!;
    this.container = getEl("table-editor-container")!;
    this.statusEl = getEl("table-editor-status")!;

    this.bindEvents();
    state.tableEditor = this;
  }

  private bindEvents(): void {
    this.closeBtn.onclick = () => this.close();
    this.loadBtn.onclick = () => {
      const group = this.groupSelect.value;
      if (!group) return;
      this.loadGroup(group);
    };
    this.saveBtn.onclick = () => this.save();
    this.addRecordBtn.onclick = () => {
      state.currentRefGroup = this.currentGroup;
      openRefRecordEditModal(false);
    };
    this.massReplaceBtn.onclick = () => this.openMassReplace();

    getButton("table-editor-bulk-delete")!.onclick = () => this.bulkDelete();
    getButton("table-editor-bulk-change-class")!.onclick = () => this.openBulkClassificationPicker();
    getButton("table-editor-bulk-edit")!.onclick = () => {
      state.currentRefGroup = this.currentGroup;
      openRefRecordEditModal(true);
    };
    getButton("table-editor-bulk-deselect")!.onclick = () => {
      this.table?.deselectRow();
      this.updateBulkActions();
    };

    this.addColumnBtn.onclick = () => this.addColumn();
    this.removeColumnBtn.onclick = () => this.removeColumn();

    const massClassSel = getSelect("ref-mass-class")!;
    massClassSel.onchange = async () => {
      const className = massClassSel.value;
      const select = getSelect("ref-mass-subclass")!;
      select.innerHTML = '<option value="">-- любой --</option>';
      if (!className) return;
      const subRes = await apiCall(`/api/reference/subclasses?class_name=${encodeURIComponent(className)}`, "GET");
      const subs = subRes.subclasses || [];
      select.innerHTML += subs.map((s: string) => `<option value="${s}">${s}</option>`).join("");
    };
    getButton("ref-mass-cancel-btn")!.onclick = () => {
      getEl<HTMLElement>("ref-mass-replace-modal")!.style.display = "none";
    };
    getButton("ref-mass-preview-btn")!.onclick = () => {
      showToast("Нажмите \"Применить\" для выполнения замены. Рекомендуется сделать бэкап.", "info");
    };
    getButton("ref-mass-apply-btn")!.onclick = () => this.applyMassReplace();
  }

  open(options: TableEditorOpenOptions): void {
    this.source = options.source;
    this.sessionId = options.sessionId || null;
    this.edits = {};
    this.currentGroup = options.group || null;
    this.isAdmin = currentUserRole === "admin";

    this.titleEl.textContent = options.title || (this.source === "ref" ? "Редактор справочника" : "Редактор потребности");
    this.addRecordBtn.style.display = this.isAdmin && this.source === "ref" ? "" : "none";
    this.massReplaceBtn.style.display = this.isAdmin ? "" : "none";
    this.columnPicker.style.display = this.isAdmin ? "flex" : "none";
    this.saveBtn.style.display = this.isAdmin ? "" : "none";

    this.populateGroupSelect();
    if (this.currentGroup) {
      this.groupSelect.value = this.currentGroup;
      void this.loadGroup(this.currentGroup);
    } else {
      this.container.innerHTML = "";
      this.table = null;
    }

    this.modal.style.display = "flex";
  }

  close(): void {
    this.modal.style.display = "none";
    if (this.table) {
      this.table.destroy();
      this.table = null;
    }
    this.edits = {};
    this.currentGroup = null;
    this.container.innerHTML = "";
  }

  private populateGroupSelect(): void {
    const groups = (state.refGroupsMeta || []) as { value: string; label: string }[];
    let options = '<option value="">-- выберите --</option>';
    if (this.source === "req" && (state.groupList || []).length) {
      options += (state.groupList as string[])
        .map((g) => {
          const label = groups.find((meta) => meta.value === g)?.label || g;
          return `<option value="${g}">${label}</option>`;
        })
        .join("");
    } else {
      options += groups.map((g) => `<option value="${g.value}">${g.label}</option>`).join("");
    }
    this.groupSelect.innerHTML = options;
  }

  private getGroupUrl(group: string, page: number, perPage: number): string {
    const encoded = encodeURIComponent(group);
    let url = `/api/table_editor/${this.source}/group/${encoded}?page=${page}&per_page=${perPage}`;
    if (this.source === "req" && this.sessionId) {
      url += `&session_id=${encodeURIComponent(this.sessionId)}`;
    }
    return url;
  }

  private getAjaxUrl(group: string): string {
    const encoded = encodeURIComponent(group);
    let url = `/api/table_editor/${this.source}/group/${encoded}`;
    if (this.source === "req" && this.sessionId) {
      url += `?session_id=${encodeURIComponent(this.sessionId)}`;
    }
    return url;
  }

  async loadGroup(group: string): Promise<void> {
    this.currentGroup = group;
    state.currentRefGroup = group;
    this.edits = {};
    this.statusEl.textContent = "Загрузка...";

    const schemaRes = await apiCall(this.getGroupUrl(group, 1, 1), "GET");
    if (!schemaRes || !schemaRes.data || !schemaRes.data.length) {
      this.statusEl.textContent = "Группа не найдена или пуста";
      showToast("Группа не найдена или пуста", "error");
      return;
    }

    const allCols = Object.keys(schemaRes.data[0] || {});
    let defaultCols: string[];
    let attrKeys: string[] = [];

    if (this.source === "ref") {
      const attrRes = await apiCall(`/api/admin/group_attributes/${encodeURIComponent(group)}`, "GET");
      const allAttrs = attrRes.attributes || [];
      attrKeys = allAttrs.map((a: any) => a.key);
      const baseCols = [
        "title_full", "group_name", "class_name", "subclass_name",
        "qty", "price", "warehouse", "contract_no", "contract_pos",
        "request_no", "request_pos", "source_filename",
      ];
      const allPossibleCols = [...new Set([...baseCols, ...attrKeys, ...allCols])];
      defaultCols = ["title_full", "group_name", "class_name", "subclass_name"].filter((c) => allPossibleCols.includes(c));
      const defaultAttrKeys = allAttrs.slice(0, 5).map((a: any) => a.key);
      defaultAttrKeys.forEach((k: string) => {
        if (allPossibleCols.includes(k) && !defaultCols.includes(k)) defaultCols.push(k);
      });
      this.allColumns = allPossibleCols;
    } else {
      defaultCols = allCols.filter((c) => !["record_id", "file_id", "title_hash"].includes(c));
      this.allColumns = allCols;
    }

    this.defaultColumns = defaultCols;
    this.extraColumns = this.allColumns.filter(
      (c) => !defaultCols.includes(c) && !["record_id", "file_id", "title_hash"].includes(c)
    );

    if (this.table) {
      this.table.destroy();
    }

    this.table = await createVirtualTable("table-editor-container", [], defaultCols, {
      editable: this.isAdmin,
      rowSelection: this.isAdmin,
      ajaxURL: this.getAjaxUrl(group),
      sampleData: schemaRes.data,
    });

    this.populateColumnPicker();
    this.bindTableEvents();
    this.setupClassificationColumns();
    this.updateBulkActions();
    this.statusEl.textContent = `Всего записей: ${schemaRes.total}`;
  }

  private setupClassificationColumns(): void {
    if (!this.table || !this.isAdmin) return;
    const fields = ["group_name", "class_name", "subclass_name"];
    for (const field of fields) {
      const col = this.table.getColumn(field);
      if (!col) continue;
      this.table.updateColumnDefinition(field, {
        editor: false,
        formatter: (cell: any) => {
          const v = cell.getValue();
          return v
            ? `<span class="class-cell" style="cursor:pointer;color:#0097D8;text-decoration:underline;">${v}</span>`
            : '<span class="class-cell" style="cursor:pointer;color:#0097D8;">--</span>';
        },
        cellClick: (_e: any, cell: any) => {
          const rowData = cell.getRow().getData();
          void this.openClassificationPickerForRow(rowData);
        },
      });
    }
  }

  private async resolveClassificationValue(
    groupName?: string,
    className?: string,
    subclassName?: string
  ): Promise<Partial<ClassificationPickerValue>> {
    const value: Partial<ClassificationPickerValue> = {};
    if (!groupName) return value;
    const groupsRes = await apiCall("/api/groups", "GET");
    const group = (groupsRes?.groups || []).find((g: any) => g.group_name === groupName);
    if (!group) return value;
    value.group_id = group.group_id;
    value.group_name = group.group_name;
    if (!className) return value;
    const classesRes = await apiCall(`/api/admin/taxonomy/classes?group_id=${group.group_id}`, "GET");
    const cls = (classesRes?.classes || []).find((c: any) => c.class_name === className);
    if (!cls) return value;
    value.class_id = cls.class_id;
    value.class_name = cls.class_name;
    if (!subclassName) return value;
    const subsRes = await apiCall(`/api/admin/taxonomy/subclasses?class_id=${cls.class_id}`, "GET");
    const sub = (subsRes?.subclasses || []).find((s: any) => s.subclass_name === subclassName);
    if (!sub) return value;
    value.subclass_id = sub.subclass_id;
    value.subclass_name = sub.subclass_name;
    return value;
  }

  private async openClassificationPickerForRow(rowData: any): Promise<void> {
    const recordId = rowData.record_id;
    if (!recordId) return;
    const initial = await this.resolveClassificationValue(
      rowData.group_name,
      rowData.class_name,
      rowData.subclass_name
    );
    classificationPicker.open({
      value: initial,
      onSave: async (newValue) => {
        const changes: Record<string, any> = {};
        if (newValue.group_name !== rowData.group_name) changes.group = newValue.group_name;
        if (newValue.class_name !== rowData.class_name) changes.class = newValue.class_name;
        if (newValue.subclass_name !== rowData.subclass_name) changes.subclass = newValue.subclass_name;
        if (!Object.keys(changes).length) return;
        const res = await apiCall(`/api/table_editor/${this.source}/records/batch_update`, "POST", {
          updates: [{ record_id: recordId, changes }],
        });
        if (res && res.success) {
          showToast("Классификация обновлена", "success");
          if (newValue.group_name !== "Не определено") {
            await this.reprocess([recordId]);
          }
          this.table?.replaceData();
          await this.refreshStats();
        } else {
          showToast("Ошибка обновления классификации", "error");
        }
      },
    });
  }

  private bindTableEvents(): void {
    if (!this.table) return;
    this.table.on("cellEdited", (cell: any) => {
      const rowData = cell.getRow().getData();
      const rid = rowData.record_id;
      const field = cell.getField();
      const value = cell.getValue();
      if (!this.edits[rid]) this.edits[rid] = {};
      this.edits[rid][field] = value;
    });
    this.table.on("rowSelected", () => this.updateBulkActions());
    this.table.on("rowDeselected", () => this.updateBulkActions());
    this.table.on("rowDblClick", (_e: any, row: any) => {
      state.currentRefGroup = this.currentGroup;
      openRefRecordEditModalSingle(row.getData());
    });
    this.table.on("pageLoaded", () => this.resetHeaderCheckbox());
    this.table.on("dataFiltered", () => this.resetHeaderCheckbox());
  }

  private resetHeaderCheckbox(): void {
    const cb = document.querySelector<HTMLInputElement>("#table-editor-container .tabulator-header input[type='checkbox']");
    if (cb) cb.checked = false;
  }

  private populateColumnPicker(): void {
    this.addColumnSelect.innerHTML =
      '<option value="">-- выберите --</option>' +
      this.extraColumns.map((c) => `<option value="${c}">${getDisplayColumnName(c)}</option>`).join("");
    this.removeColumnSelect.innerHTML =
      '<option value="">-- выберите --</option>' +
      this.defaultColumns.map((c) => `<option value="${c}">${getDisplayColumnName(c)}</option>`).join("");
  }

  addColumn(key?: string): void {
    const select = this.addColumnSelect;
    const custom = this.addColumnCustom;
    const colKey = key || select.value || custom.value.trim();
    if (!colKey) return;
    if (!this.table) return;

    const existing = this.table.getColumn(colKey);
    if (existing) {
      existing.show();
    } else {
      this.table.addColumn({
        title: getDisplayColumnName(colKey),
        field: colKey,
        editor: this.isAdmin ? "input" : false,
        headerFilter: "input",
        minWidth: 100,
        resizable: true,
      });
    }
    this.table.redraw(true);

    const option = select.querySelector<HTMLOptionElement>(`option[value="${colKey}"]`);
    if (option) option.remove();
    select.value = "";
    custom.value = "";

    if (!this.removeColumnSelect.querySelector<HTMLOptionElement>(`option[value="${colKey}"]`)) {
      const opt = document.createElement("option");
      opt.value = colKey;
      opt.textContent = getDisplayColumnName(colKey);
      this.removeColumnSelect.appendChild(opt);
    }
  }

  removeColumn(): void {
    const key = this.removeColumnSelect.value;
    if (!key || !this.table) return;
    const col = this.table.getColumn(key);
    if (col) {
      this.table.deleteColumn(key);
      this.table.redraw(true);
    }
    const option = this.removeColumnSelect.querySelector<HTMLOptionElement>(`option[value="${key}"]`);
    if (option) option.remove();
    this.removeColumnSelect.value = "";

    if (!this.addColumnSelect.querySelector<HTMLOptionElement>(`option[value="${key}"]`)) {
      const opt = document.createElement("option");
      opt.value = key;
      opt.textContent = getDisplayColumnName(key);
      this.addColumnSelect.appendChild(opt);
    }
  }

  async save(): Promise<void> {
    const editKeys = Object.keys(this.edits);
    if (!editKeys.length) {
      showToast("Нет изменений для сохранения", "info");
      return;
    }
    const recordIds = editKeys.map((rid) => parseInt(rid, 10));
    const data = recordIds.map((rid) => ({ record_id: rid, ...this.edits[String(rid)] }));
    const res = await apiCall(`/api/table_editor/${this.source}/save_edits`, "POST", { data });
    if (res && res.success) {
      this.edits = {};
      showToast(`Правки сохранены, обновлено ${res.updated} записей`, "success");
      await this.reprocess(recordIds);
      this.table?.replaceData();
      await this.refreshStats();
    } else {
      showToast("Ошибка сохранения", "error");
    }
  }

  updateBulkActions(): void {
    const selected = this.table?.getSelectedRows() || [];
    const count = selected.length;
    this.selectedCount.textContent = `Выбрано: ${count}`;
    this.bulkActions.style.display = count > 0 ? "flex" : "none";
  }

  async bulkDelete(): Promise<void> {
    const selected = this.table?.getSelectedData() || [];
    if (!selected.length) return;
    const recordIds = selected.map((r: any) => r.record_id).filter(Boolean);
    if (!recordIds.length) return;
    if (!confirm(`Удалить ${recordIds.length} записей?`)) return;
    const res = await apiCall(`/api/table_editor/${this.source}/records/delete`, "POST", { record_ids: recordIds });
    if (res && res.success) {
      showToast(`Удалено ${res.deleted} записей`, "success");
      this.table?.replaceData();
      this.updateBulkActions();
    } else {
      showToast("Ошибка удаления", "error");
    }
  }

  // ---------- Bulk classification picker ----------

  async openBulkClassificationPicker(): Promise<void> {
    const selected = this.table?.getSelectedData() || [];
    if (!selected.length) {
      showToast("Сначала выберите записи", "error");
      return;
    }
    const recordIds = selected.map((r: any) => r.record_id).filter(Boolean) as number[];
    if (!recordIds.length) return;

    const uniqueGroups = [...new Set(selected.map((r: any) => r.group_name).filter(Boolean))];
    const uniqueClasses = [...new Set(selected.map((r: any) => r.class_name).filter(Boolean))];
    const uniqueSubclasses = [...new Set(selected.map((r: any) => r.subclass_name).filter(Boolean))];

    const currentGroup = uniqueGroups.length === 1 ? (uniqueGroups[0] as string) : undefined;
    const currentClass = uniqueClasses.length === 1 ? (uniqueClasses[0] as string) : undefined;
    const currentSubclass = uniqueSubclasses.length === 1 ? (uniqueSubclasses[0] as string) : undefined;

    const initial = await this.resolveClassificationValue(currentGroup, currentClass, currentSubclass);
    classificationPicker.open({
      value: initial,
      onSave: async (newValue) => {
        const changes: Record<string, any> = {};
        if (newValue.group_name !== currentGroup) changes.group = newValue.group_name;
        if (newValue.class_name !== currentClass) changes.class = newValue.class_name;
        if (newValue.subclass_name !== currentSubclass) changes.subclass = newValue.subclass_name;
        if (!Object.keys(changes).length) return;
        const updates = recordIds.map((rid) => ({ record_id: rid, changes }));
        const res = await apiCall(`/api/table_editor/${this.source}/records/batch_update`, "POST", { updates });
        if (res && res.success) {
          showToast(`Обновлено ${recordIds.length} записей`, "success");
          if (newValue.group_name !== "Не определено") {
            await this.reprocess(recordIds);
          }
          this.table?.replaceData();
          await this.refreshStats();
        } else {
          showToast("Ошибка обновления классификации", "error");
        }
      },
    });
  }

  // ---------- Mass replace ----------

  async openMassReplace(): Promise<void> {
    const group = this.currentGroup;
    if (!group) {
      showToast("Сначала загрузите группу", "error");
      return;
    }
    let options = "";
    if (this.source === "ref") {
      const attrRes = await apiCall(`/api/admin/group_attributes/${encodeURIComponent(group)}`, "GET");
      const attrs = attrRes.attributes || [];
      options = attrs.map((a: any) => `<option value="${a.key}">${a.label}</option>`).join("");
    } else {
      options = this.allColumns
        .filter((c) => !["record_id", "file_id", "title_hash", "source_filename"].includes(c))
        .map((c) => `<option value="${c}">${getDisplayColumnName(c)}</option>`)
        .join("");
    }
    getSelect("ref-mass-attr")!.innerHTML = options;
    const classesRes = await apiCall(`/api/reference/classes?group=${encodeURIComponent(group)}`, "GET");
    const classes = classesRes.classes || [];
    getSelect("ref-mass-class")!.innerHTML =
      '<option value="">-- любой --</option>' + classes.map((c: string) => `<option value="${c}">${c}</option>`).join("");
    getSelect("ref-mass-subclass")!.innerHTML = '<option value="">-- любой --</option>';
    getEl<HTMLElement>("ref-mass-preview")!.innerHTML = "";
    getEl<HTMLElement>("ref-mass-replace-modal")!.style.display = "flex";
  }

  async applyMassReplace(): Promise<void> {
    const group = this.currentGroup;
    const attrName = getSelect("ref-mass-attr")!.value;
    const oldValue = getInput("ref-mass-old")!.value;
    const newValue = getInput("ref-mass-new")!.value;
    const className = getSelect("ref-mass-class")!.value;
    const subclassName = getSelect("ref-mass-subclass")!.value;
    if (!group || !attrName || oldValue === "" || newValue === "") {
      showToast("Заполните все обязательные поля", "error");
      return;
    }
    const payload: Record<string, any> = {
      group,
      attr_name: attrName,
      old_value: oldValue,
      new_value: newValue,
    };
    if (className) payload.class = className;
    if (subclassName) payload.subclass = subclassName;
    if (this.source === "req" && this.sessionId) {
      payload.session_id = this.sessionId;
    }
    const res = await apiCall(`/api/table_editor/${this.source}/records/mass_update`, "POST", payload);
    if (res && res.success) {
      showToast(`Заменено ${res.updated} записей`, "success");
      getEl<HTMLElement>("ref-mass-replace-modal")!.style.display = "none";
      const ids: number[] = Array.isArray(res.record_ids) ? res.record_ids : [];
      if (ids.length) {
        await this.reprocess(ids);
      }
      this.table?.replaceData();
      await this.refreshStats();
    }
  }

  async reprocess(recordIds: number[]): Promise<void> {
    if (!recordIds.length) return;
    this.statusEl.textContent = "Переобработка записей...";
    const payload: Record<string, any> = { record_ids: recordIds };
    if (this.source === "req" && this.sessionId) {
      payload.session_id = this.sessionId;
    }
    const res = await apiCall(`/api/table_editor/${this.source}/records/reprocess`, "POST", payload);
    if (res && res.success) {
      showToast(`Переобработано ${res.updated} записей`, "success");
    } else {
      showToast("Ошибка переобработки", "error");
    }
    this.statusEl.textContent = "";
  }

  async refreshStats(): Promise<void> {
    if (this.source !== "req" || !this.sessionId) return;
    const res = await apiCall(`/api/table_editor/req/stats?session_id=${encodeURIComponent(this.sessionId)}`, "GET");
    if (res && res.stats) {
      state.classifiedStats = res.stats;
      if (typeof (window as any).renderMassGroupsInfo === "function") {
        (window as any).renderMassGroupsInfo();
      }
    }
  }

  replaceData(): void {
    this.table?.replaceData();
  }
}
